import React, { useRef, useState } from 'react'
import { Box, Chip, IconButton, Popover, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { Loader } from 'src/designSystem'
import { AssignableQuestionnaire } from 'src/graphql.generated'
import { QuestionMark } from '@mui/icons-material'
import {
  PatientQuestionnaireAssignmentsDocument,
  useAssignQuestionnaireToPatientMutation,
  usePatientQuestionnaireAssignmentsQuery,
  useRemoveQuestionnaireAssignmentMutation,
} from './questionnaireAssignmentQueries.generated'
import { useGetPatientWithTagsQuery } from '../patientQueries.generated'

const ADVANCED_PROGRAM_TAG_NAME = 'ADVANCED_PROGRAM'

const useStyles = makeStyles((theme) => ({
  popover: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2),
  },
  chipSpacing: {
    padding: theme.spacing(0.5),
  },
}))

const QUESTIONNAIRE_LABELS: Record<AssignableQuestionnaire, string> = {
  [AssignableQuestionnaire.Pcl5]: 'PCL-5',
  [AssignableQuestionnaire.Asrm]: 'ASRM',
  [AssignableQuestionnaire.Ocir]: 'OCI-R',
  [AssignableQuestionnaire.Isi]: 'ISI',
}

type Props = {
  uid: string
}

const ParticipantQuestionnaires = ({ uid }: Props) => {
  const styles = useStyles()
  const { data, loading } = usePatientQuestionnaireAssignmentsQuery({ variables: { uid } })
  const { data: patientData, loading: patientLoading } = useGetPatientWithTagsQuery({ variables: { uid } })
  const [assignQuestionnaire] = useAssignQuestionnaireToPatientMutation()
  const [removeQuestionnaireAssignment] = useRemoveQuestionnaireAssignmentMutation()

  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLParagraphElement>(null)

  if ((loading || patientLoading) && !open) {
    return <Loader size="large" />
  }

  const isAdvancedPatient = (patientData?.patient?.tags || []).some((tag) => tag.name === ADVANCED_PROGRAM_TAG_NAME)
  if (!isAdvancedPatient) {
    return null
  }

  const assignments = data?.patientQuestionnaireAssignments || []
  const refetchQueries = [{ query: PatientQuestionnaireAssignmentsDocument, variables: { uid } }]
  const openAssignmentFor = (questionnaire: AssignableQuestionnaire) =>
    assignments.find((assignment) => assignment.questionnaire === questionnaire && !assignment.completedAt)

  const handleSelect = (questionnaire: AssignableQuestionnaire) => async () => {
    const existing = openAssignmentFor(questionnaire)
    if (existing) {
      await removeQuestionnaireAssignment({ variables: { uid, assignmentID: existing.assignmentID }, refetchQueries })
    } else {
      await assignQuestionnaire({ variables: { uid, questionnaire }, refetchQueries })
    }
  }

  const handleClick = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <Box display="inline-flex" alignItems="center" flexWrap="wrap">
      <Typography ref={anchorRef}>
        Assign questionnaire(s)
        <IconButton onClick={handleClick} size="small">
          <QuestionMark />
        </IconButton>
      </Typography>
      {assignments.map((assignment) => (
        <Box className={styles.chipSpacing} key={assignment.assignmentID}>
          <Chip
            color={assignment.completedAt ? 'default' : 'primary'}
            label={`${QUESTIONNAIRE_LABELS[assignment.questionnaire]}${assignment.completedAt ? ' (completed)' : ''}`}
          />
        </Box>
      ))}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box className={styles.popover}>
          {Object.values(AssignableQuestionnaire).map((questionnaire) => {
            const isAssigned = Boolean(openAssignmentFor(questionnaire))
            return (
              <Box className={styles.chipSpacing} key={questionnaire}>
                <Chip
                  clickable
                  color={isAssigned ? 'primary' : undefined}
                  label={QUESTIONNAIRE_LABELS[questionnaire]}
                  variant={isAssigned ? undefined : 'outlined'}
                  onClick={handleSelect(questionnaire)}
                />
              </Box>
            )
          })}
        </Box>
      </Popover>
    </Box>
  )
}

export default ParticipantQuestionnaires
